"use client";

import { useEffect, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { MessageCircle } from "lucide-react";
import { cn } from "../common/utils";
import { useFeatureGate } from "statsig-react";

interface CommentCountProps {
  videoId: string;
  showAvatars?: boolean;
  className?: string;
  href?: string;
}

interface CommentUser {
  id: string;
  username: string;
  avatar_url: string | null;
}

export default function CommentCount({
  videoId,
  showAvatars = true,
  className = "",
  href,
}: CommentCountProps) {
  const [count, setCount] = useState(0);
  const [hasUserCommented, setHasUserCommented] = useState(false);
  const [recentCommenters] = useState<CommentUser[]>([]);
  const [loading, setLoading] = useState(true);

  // Double-check feature flag as a safeguard
  const { gate } = useFeatureGate("video_comments_stableid");
  const isCommentsEnabled = gate?.value === true;

  useEffect(() => {
    if (isCommentsEnabled) {
      fetchCommentData();
    }
  }, [videoId, isCommentsEnabled]);

  const fetchCommentData = async () => {
    try {
      const response = await fetch(`/api/comments/count?content_id=${videoId}`);

      if (!response.ok) {
        throw new Error("Failed to fetch comment count");
      }

      const data = await response.json();
      setCount(data.count || 0);
      setHasUserCommented(data.hasUserCommented || false);

      // TODO: Add avatar display when user profiles are implemented
    } catch (error) {
      console.error("Error fetching comment data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Return early if comments are not enabled or still loading
  if (!isCommentsEnabled || loading) {
    return null;
  }

  // Determine button colors based on whether user has commented
  const getButtonColors = () => {
    if (hasUserCommented) {
      return "fill-primary text-primary bg-primary/6 hover:bg-primary/8 focus:bg-primary/10";
    }
    return "fill-primary-blue text-primary-blue bg-black/6 hover:bg-black/8 focus:bg-black/10";
  };

  const ButtonContent = (
    <>
      {showAvatars && recentCommenters.length > 0 && (
        <div className="flex -space-x-2">
          {recentCommenters.map((commenter, index) => (
            <div
              key={commenter.id}
              className="relative"
              style={{ zIndex: recentCommenters.length - index }}
            >
              {commenter.avatar_url ? (
                <Image
                  src={commenter.avatar_url}
                  alt={commenter.username}
                  width={24}
                  height={24}
                  className="rounded-full border-2 border-white"
                />
              ) : (
                <div className="w-6 h-6 bg-gray-300 rounded-full border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-medium text-gray-600">
                    {commenter.username[0]?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <MessageCircle className="w-6 h-6" />
      {count > 0 && (
        <span className="text-[16px] md:text-lg font-semibold leading-none">
          {count}
        </span>
      )}
    </>
  );

  const buttonClasses = cn(
    "flex flex-row items-center gap-0.5 py-[5px] md:py-1 px-2.5 md:px-2 rounded-md transition-colors duration-150 ease-in-out select-none",
    getButtonColors(),
    className
  );

  if (href) {
    return (
      <Link href={href} className={buttonClasses}>
        {ButtonContent}
      </Link>
    );
  }

  return (
    <button className={buttonClasses} type="button">
      {ButtonContent}
    </button>
  );
}
